/**
 * diagnosticTranslations.ts
 *
 * Translation layer: maps raw technical dimension keys and metric values into
 * coaching-language sentences for display in the DebugDiagnosticsPanel.
 *
 * OWNER RULING (2026-07-25, §3.4 Q4): Translation layer is DEFERRED until all
 * changes are in and stabilized (post BD-7 capture-sprint stability). PR 4 keeps
 * this as stubs returning null. Do NOT populate translations here until the owner
 * explicitly approves PR 4.
 *
 * When this is populated (PR 4), the functions return a coaching-language string.
 * The panel renders the technical value first, then the translation in italic below.
 */

// ============================================================================
// Types
// ============================================================================

/** Per-dimension BD-7 pricing result entry. */
export interface PricingDimEntry {
  z: number | null;
  score: number | null;
  verdict: 'scoreable' | 'severe' | '0-class';
  Kc: number | null;
  Ks: number | null;
}

/**
 * Translate a BD-7 dimension key + z-score into a coaching-language sentence.
 *
 * @param dimensionKey - e.g. "B_motion", "A_orientation", "F_foot_direction"
 * @param zScore       - the raw z-score from pricing_result.per_dim_scores[key].z
 * @param Kc           - the Kc boundary from the dimension entry (null if not minted)
 * @param Ks           - the Ks boundary from the dimension entry (null if not minted)
 * @param templateMeta - optional template metadata for context-aware translations
 * @returns coaching-language string, or null if no translation is registered for this dimension
 *
 * FUTURE (PR 4): populate each dimension branch when the owner approves translation strings.
 */
export function translateDimension(
  _dimensionKey: string,
  _zScore: number | null,
  _Kc?: number | null,
  _Ks?: number | null,
  _templateMeta?: Record<string, unknown>
): string | null {
  // FUTURE: translation layer — deferred per owner ruling (2026-07-25, §3.4 Q4).
  // Return null for all dimensions until PR 4.
  return null;
}

/**
 * Human-readable label for a BD-7 dimension key.
 * Used in the panel table header.
 *
 * @param dimensionKey - e.g. "B_motion", "A_orientation"
 * @returns human-readable label, or the raw key as fallback
 */
export function dimensionLabel(dimensionKey: string): string {
  const labels: Record<string, string> = {
    A_orientation: 'Body Orientation',
    B_motion: 'Motion Orchestration',
    // CORRECTED (EXP-009 §3.4 / PRIORITIES D-E, 2026-10-10): the backend's
    // _DIM_NAMES (calibration_worker.py's _mint_pricing_block) emits
    // "E_head_dip" and "F_foot_dir", never the shorter "E_head"/"F_foot"
    // these two keys used to be. The mismatch meant 8 of 10 zero-class
    // banners measured on EXP-009's corpus printed the raw id verbatim
    // (e.g. "E_head_dip was a long way from your coach's demo"). Keep the
    // old short keys as ALIASES below only if proven live elsewhere — grep
    // confirmed (2026-10-10) `calibration_worker.py`, `pricing_engine.py`
    // and every frontend caller use the full ids below exclusively, so no
    // alias is added; a future producer that still emits the short form
    // would be its own bug, not something this map should paper over.
    E_head_dip: 'Head Dip',
    F_foot_dir: 'Foot Direction',
    H_heel_rise: 'Heel Rise',
    L4_orchestration: 'Movement Coordination',
  };
  return labels[dimensionKey] ?? dimensionKey;
}

/**
 * COACH- AND ATHLETE-FACING label for a BD-7 dimension key.
 *
 * `dimensionLabel` above is the ENGINEERING label — it is what the coach-only
 * DebugDiagnosticsPanel table wants, and "Motion Orchestration" / "Body
 * Orientation" are the names used in the design docs and the backend. Those
 * are internal names, which docs/COACH_FACING_COPY_STANDARD.md §2 forbids in
 * copy a coach or athlete reads, so the report says the same thing in the
 * words a coach would use. Owner-approved mapping, standard §3:
 * "Motion Orchestration and Body Orientation" -> "Timing and body position".
 *
 * Lower case because these phrases are composed into a list (see
 * ZeroClassBanner's buildDimList, which sentence-cases the first one). An
 * unknown key falls back to the engineering label rather than a raw key —
 * still not ideal, but a readable approximation beats "X_unknown_dim".
 */
export function coachFacingDimensionLabel(dimensionKey: string): string {
  const labels: Record<string, string> = {
    A_orientation: 'body position',
    B_motion: 'timing',
    // CORRECTED (EXP-009 §3.4 / PRIORITIES D-E, 2026-10-10) — see the
    // matching comment on dimensionLabel() above: the backend emits
    // "E_head_dip" / "F_foot_dir", never the short forms these two keys
    // used to be, so 8 of 10 zero-class banners in the EXP-009 corpus
    // printed the raw id to the athlete instead of this phrase.
    E_head_dip: 'head position',
    F_foot_dir: 'foot direction',
    H_heel_rise: 'heel lift',
    L4_orchestration: 'movement coordination',
  };
  return labels[dimensionKey] ?? dimensionLabel(dimensionKey);
}

/**
 * Human-readable verdict label for a per-dimension verdict string.
 *
 * @param verdict - 'scoreable' | 'severe' | '0-class'
 * @returns display string
 */
export function verdictLabel(verdict: string): string {
  switch (verdict) {
    case 'scoreable':
      return 'Good';
    case 'severe':
      return 'Flagged';
    case '0-class':
      return '0-class trigger';
    default:
      return verdict;
  }
}

/**
 * CSS color token for a per-dimension verdict, for use in className or inline style.
 *
 * @param verdict - 'scoreable' | 'severe' | '0-class'
 * @returns Tailwind text color class
 */
export function verdictColorClass(verdict: string): string {
  switch (verdict) {
    case 'scoreable':
      return 'text-success';
    case 'severe':
      return 'text-warning';
    case '0-class':
      return 'text-danger';
    default:
      return 'text-text-muted';
  }
}
